import assetUtils, { Asset } from 'app-lib/assetUtils'
import React, { useRef, useCallback } from 'react'
import { Icon, Tooltip } from 'ui-framework'
import { BigNumber } from '@0x/utils'
import AssetDropdownBalance from './AssetDropdownBalance'
import AssetDropdownSimple from './AssetDropdownSimple'

export interface IAssetDropdownProps {
  className?: string
  assets?: Asset[]
  assetsWithBalance?: { token: Asset; amount: BigNumber }[]
  selectedAsset: Asset
  onAssetChange?: (asset: Asset) => void
  emptyLabel?: string
  emptyIcon?: string
}

/**
 * AssetDropdown can work in 2 ways
 * - simple asset list (use props.assets)
 * - asset list that includes balance (user props.assetsWithBalance)
 */
export default function AssetDropdown(props: IAssetDropdownProps) {
  const { current: tipId } = useRef('assetdropdown' + Math.random())
  const onSelect = useCallback(
    (asset: Asset) => {
      if (props.onAssetChange) {
        props.onAssetChange(asset)
      }
    },
    [props.onAssetChange]
  )
  const { displayName, spriteLogo } = assetUtils.getCommon(props.selectedAsset)
  const { assets, assetsWithBalance } = props

  const hasOnlyOneChoice =
    (!!assets && assets.length <= 1) || (!!assetsWithBalance && assetsWithBalance.length <= 1)
  const iconName = spriteLogo || props.emptyIcon
  const btnClassName = props.className || ''

  const triggerButton = (
    <div
      data-tip
      data-for={tipId}
      data-event="click focus"
      className={`asset-dropdown-button ${btnClassName}  ${hasOnlyOneChoice ? 'empty' : ''}`}>
      {iconName && <Icon name={iconName} size={24} className="margin-right-05" />}
      <b className="asset-dropdown__btn-label">{displayName || props.emptyLabel}</b>
    </div>
  )

  if (hasOnlyOneChoice) {
    return triggerButton
  }

  return (
    <>
      {triggerButton}
      <Tooltip
        id={tipId}
        className="tooltip--default"
        place="bottom"
        clickable
        effect="solid"
        isCapture
        globalEventOff="click"
        data-event-off="click"
        delayHide={200}>
        <div
          className={
            Array.isArray(props.assetsWithBalance)
              ? 'asset-dropdown--with-balance'
              : 'asset-dropdown'
          }>
          <div className="asset-dropdown__items">
            {Array.isArray(props.assets) && (
              <AssetDropdownSimple
                assets={props.assets}
                onSelect={onSelect}
                selectedAsset={props.selectedAsset}
                emptyLabel={props.emptyLabel}
                emptyIcon={props.emptyIcon}
              />
            )}
            {Array.isArray(props.assetsWithBalance) && (
              <AssetDropdownBalance
                assetsWithBalance={props.assetsWithBalance}
                onSelect={onSelect}
                selectedAsset={props.selectedAsset}
              />
            )}
          </div>
        </div>
      </Tooltip>
    </>
  )
}
